import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from 'reactstrap';
import moment from 'moment';
import toast from 'react-hot-toast';
import {
  PAYMENT_FAILED_AGENT_STATUSES,
  PF_ACTION_PLACED_NEW_ORDER,
  PF_ACTION_REPROCESSED_SUCCESSFULLY,
  PF_OUTCOME_CONVERTED,
  PF_OUTCOME_NOT_CONVERTED,
  getPaymentFailedStatusChipClass,
  getPaymentFailedStatusLabel,
  getPaymentFailedTrigger,
  isPaymentFailedClosingStatus,
  isUrgentTrigger,
  requiresResolvedOrderNo,
} from '../../../constants/paymentFailedOrders';
import {
  fetchPaymentFailedOrderDetail,
  updatePaymentFailedOrderAction,
} from '../actions/action';
import { clearPaymentFailedOrderDetail } from '../../../Redux/paymentFailedOrders/paymentFailedOrdersSlice';

const toastError = {
  style: {
    border: '1px solid #fff',
    padding: '20px',
    color: '#ffffff',
    background: '#f44336',
  },
  iconTheme: { primary: '#FFFAEE', secondary: '#f44336' },
};

const toastSuccess = {
  style: {
    border: '1px solid #fff',
    padding: '20px',
    color: '#ffffff',
    background: '#4CAF50',
  },
  iconTheme: { primary: '#FFFAEE', secondary: '#4CAF50' },
};

const formatDateTime = (value) =>
  value ? moment(new Date(value)).format('DD MMM YYYY, HH:mm') : '-';


// Rendered as the contents of the right hand drawer, one record at a time
const PaymentFailedOrderDetail = ({ record, onClose, onUpdated, onDemoSave }) => {
  const dispatch = useDispatch();
  const { detail, isDetailLoading } =
    useSelector((state) => state.paymentFailedOrders) || {};

  const [actionStatus, setActionStatus] = useState('');
  const [remarks, setRemarks] = useState('');
  const [resolvedOrderNo, setResolvedOrderNo] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const recordId = record?.id;
  const isDemoRecord = Boolean(record?.__demo);

  useEffect(() => {
    if (!recordId || isDemoRecord) return undefined;
    fetchPaymentFailedOrderDetail({ params: { id: recordId }, dispatch }).catch(() =>
      toast.error('Failed to load the record.', toastError),
    );
    return () => dispatch(clearPaymentFailedOrderDetail());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId, isDemoRecord]);

  // Show the list row immediately, swap in the details response when it lands
  const data = (!isDemoRecord && detail) || record || {};
  const history = data?.history || [];
  const closed = Boolean(data?.closed_at);
  const trigger = getPaymentFailedTrigger(data?.trigger_reason);
  const needsOrderNo = requiresResolvedOrderNo(actionStatus);
  const loading = !isDemoRecord && isDetailLoading && !detail;

  const user = JSON.parse(localStorage?.getItem('authUser') || '{}');

  const handleSubmit = async () => {
    if (!actionStatus) {
      setFormError('Select an action status.');
      return;
    }
    if (!remarks.trim()) {
      setFormError('Remarks are required.');
      return;
    }
    if (needsOrderNo && !resolvedOrderNo.trim()) {
      setFormError(
        'Enter the new order reference - without it this record is not counted as a recovered sale.',
      );
      return;
    }
    setFormError('');

    // Demo rows live in memory only - update them in place so the flow can be clicked through
    if (isDemoRecord) {
      const closesNow = isPaymentFailedClosingStatus(actionStatus);
      const converts =
        actionStatus === PF_ACTION_PLACED_NEW_ORDER ||
        actionStatus === PF_ACTION_REPROCESSED_SUCCESSFULLY;
      const now = moment().format('YYYY-MM-DD HH:mm:ss');
      if (onDemoSave) {
        onDemoSave(recordId, {
          action_status: actionStatus,
          remarks: remarks.trim(),
          resolved_order_no: needsOrderNo ? resolvedOrderNo.trim() : '',
          updated_by_username: user?.user_name || 'you',
          updated_at: now,
          closed_at: closesNow ? now : null,
          outcome_type: closesNow
            ? converts
              ? PF_OUTCOME_CONVERTED
              : PF_OUTCOME_NOT_CONVERTED
            : null,
          history: [
            {
              id: (history.length || 0) + 1,
              action_status: actionStatus,
              remarks: remarks.trim(),
              updated_by_username: user?.user_name || 'you',
              created_at: now,
            },
            ...history,
          ],
        });
      }
      toast.success('Action saved (demo data).', toastSuccess);
      if (onClose) onClose();
      return;
    }

    try {
      setSaving(true);
      const response = await updatePaymentFailedOrderAction({
        params: {
          id: recordId,
          action_status: actionStatus,
          remarks: remarks.trim(),
          resolved_order_no: needsOrderNo ? resolvedOrderNo.trim() : '',
          oms_user_id: user?.user_id || '',
          updated_by_username: user?.user_name || '',
        },
        dispatch,
      });
      if (response?.code === 200 || response?.code === 100) {
        toast.success('Action saved.', toastSuccess);
        if (onUpdated) onUpdated();
        if (onClose) onClose();
      } else {
        toast.error(response?.message || 'Failed to save the action.', toastError);
      }
    } catch (error) {
      toast.error('Failed to save the action.', toastError);
    } finally {
      setSaving(false);
    }
  };

  const historySection = (
    <>
            <h6 className="mb-2">Action History</h6>
            {history.length > 0 ? (
              <div>
                {history.map((step, index) => (
                  <div
                    key={step?.id || index}
                    className={`pf-history-item ${index === 0 ? 'pf-history-latest' : ''}`}
                  >
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <span
                        className={`pf-chip ${getPaymentFailedStatusChipClass(
                          step?.action_status,
                        )}`}
                      >
                        {getPaymentFailedStatusLabel(step?.action_status)}
                      </span>
                      <span className="pf-cell-muted">
                        {formatDateTime(step?.created_at)}
                      </span>
                      <span className="pf-cell-muted">
                        {step?.updated_by_username || 'System'}
                      </span>
                    </div>
                    {step?.remarks && (
                      <div className="mt-1" style={{ fontSize: 12 }}>
                        {step.remarks}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="pf-cell-muted mb-0">No actions recorded yet.</p>
            )}
    </>
  );

  return (
    <div className="pf-drawer">
      <div className="pf-drawer-header d-flex align-items-center justify-content-between px-3 py-2">
        <div className="d-flex align-items-center flex-wrap gap-2">
          <strong>{data?.reference_no || 'Payment Failed Order'}</strong>
          <span className={`pf-chip ${getPaymentFailedStatusChipClass(data?.action_status)}`}>
            {getPaymentFailedStatusLabel(data?.action_status)}
          </span>
          {isUrgentTrigger(data?.trigger_reason) && !closed && (
            <span className="pf-badge-urgent" title={trigger?.hint}>
              Urgent · {trigger?.shortLabel}
            </span>
          )}
        </div>
        <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
      </div>

      <div className="pf-drawer-body px-3 py-3">
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <>
            {closed && (
              <>
                {historySection}
                <hr className="my-3" />
              </>
            )}

            <h6 className="mb-2">Update Action</h6>
            {closed ? (
              <div className="pf-kpi-card mb-3">
                <div className="pf-kpi-label">Record closed</div>
                <div style={{ fontSize: 12 }}>
                  Closed on {formatDateTime(data.closed_at)} as{' '}
                  <strong>{getPaymentFailedStatusLabel(data?.action_status)}</strong>. Closed
                  records cannot be reopened from here.
                </div>
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <label className="pf-detail-label" htmlFor="pfActionStatus">
                    Action Status <span className="text-danger">*</span>
                  </label>
                  <select
                    id="pfActionStatus"
                    className="form-select"
                    value={actionStatus}
                    onChange={(e) => setActionStatus(e.target.value)}
                  >
                    <option value="">Select action…</option>
                    {PAYMENT_FAILED_AGENT_STATUSES.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                  {isPaymentFailedClosingStatus(actionStatus) && (
                    <div className="pf-cell-muted mt-1">This action closes the record.</div>
                  )}
                </div>

                {needsOrderNo && (
                  <div className="mb-3">
                    <label className="pf-detail-label" htmlFor="pfResolvedOrderNo">
                      New Order Reference <span className="text-danger">*</span>
                    </label>
                    <input
                      id="pfResolvedOrderNo"
                      type="text"
                      className="form-control"
                      placeholder="e.g. 100045678"
                      value={resolvedOrderNo}
                      onChange={(e) => setResolvedOrderNo(e.target.value)}
                    />
                    <div className="pf-cell-muted mt-1">
                      Required - without it this episode is not counted as a recovered sale
                      in reporting.
                    </div>
                  </div>
                )}

                <div className="mb-3">
                  <label className="pf-detail-label" htmlFor="pfRemarks">
                    Remarks <span className="text-danger">*</span>
                  </label>
                  <textarea
                    id="pfRemarks"
                    className="form-control"
                    rows={4}
                    placeholder="What happened on the call?"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                  />
                </div>

                {formError && (
                  <div className="text-danger mb-2" style={{ fontSize: 12 }}>
                    {formError}
                  </div>
                )}
              </>
            )}

            {!closed && (
              <>
                <hr className="my-3" />
                {historySection}
              </>
            )}
          </>
        )}
      </div>

      <div className="pf-drawer-footer d-flex justify-content-end gap-2 px-3 py-2">
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
          Close
        </button>
        {!closed && !loading && (
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save Action'}
          </button>
        )}
      </div>
    </div>
  );
};

export default PaymentFailedOrderDetail;
